import { Guid } from "./guid";
import { ProtocolConfiguration, ProtocolMessage, MessageData, buildSerializer, PayloadSerializer, PayloadDeserializer, DeserializerContext, buildDeserializer } from "./protocol";
import { ByteOrByteArray, encode, Writer } from "./writer";



// Identifies the local avatar
export const myself : Guid = new Guid("ffffffffffffffffffffffffffffffff");

// Identifies a message's emitter
export const themselves : Guid = new Guid("eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee");


export type RemoteMediaStreamTrack = MediaStreamTrack & { 
	sourceId : Guid | undefined, 
	sourceMid : string | undefined 
};


interface ClientProtocol
{
	Offer : ProtocolMessage;
	Answer : ProtocolMessage;
	Error : ProtocolMessage;
	Ready : ProtocolMessage;
	ForwardTrack : ProtocolMessage;
	ReceiveTrack : ProtocolMessage;
};
interface OfferPayload { sdp : string; };
interface AnswerPayload { sdp : string; };
interface ErrorPayload { message : string; }
interface ForwardTrackPayload { mid : string; emitterId : Guid; emitterMid : string; }
interface ReceiveTrackPayload { mid : string; }
interface ReadyPayload { };

export class RTCClient // implements EventTarget
{
	private eventTarget : EventTarget;
	
	public connection? : RTCPeerConnection;
	public dataChannel? : RTCDataChannel;
	public protocol? : ProtocolConfiguration;

	public tracks : { [mid:string] : RemoteMediaStreamTrack } = {}

	public protocolSerializers : PayloadSerializer[] = [];
	public protocolDeserializers : PayloadDeserializer[] = [];

	constructor()
	{
		this.eventTarget = document.createDocumentFragment();
	}
	addEventListener(type: string, callback: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions | undefined): void {
		this.eventTarget.addEventListener(type, callback, options);
	}
	dispatchEvent(event: Event): boolean {
		return this.eventTarget.dispatchEvent(event);
	}
	removeEventListener(type: string, callback: EventListenerOrEventListenerObject | null, options?: boolean | EventListenerOptions | undefined): void {
		this.eventTarget.removeEventListener(type, callback, options);
	}

	//( name : string ) { this.sendRaw( "+".charCodeAt(0), encode(name), 0); }
	//leaveChannel( name : string ) { this.sendRaw( "-".charCodeAt(0), encode(name), 0); }

	addLocalTrack( track : MediaStreamTrack  ) : Promise<string>
	{
		return new Promise<string>( (y,n) =>
		{
			const pc = this.connection!;

			pc.addTrack(track);

			const trn = pc.getTransceivers().find( t => t.sender.track === track );
			if(!trn)
				return n("AddTrack didn't add a transceiver :(");
			
			const listener = (e : CustomEvent) =>
			{
				if(e.detail != trn.mid) return;
				this.eventTarget.removeEventListener("track-received", listener as EventListener);
				y(trn.mid!);
			};
			this.eventTarget.addEventListener("track-received", listener as EventListener);
		});
	}

	sendRaw( ...parts : ByteOrByteArray[] )
	{
		this.dataChannel!.send(Writer.join(...parts));
	}


	deserialize(message : ProtocolMessage, payload : Uint8Array, context? : any, into? : any) : any
	{
		const ctx : DeserializerContext = { message : payload };
		if(context)
			for(let key in context)
				(ctx as any)[key] = context[key];
		
		const deserializer = this.protocolDeserializers[ message.code ] ??
			(this.protocolDeserializers[ message.code ] = buildDeserializer("Object", message.payload ));

		return deserializer(ctx, into);
	}

	serializeMessage(message : ProtocolMessage, body : any, parts : ByteOrByteArray[], baseLength : number )
	{
		const serializer = this.protocolSerializers[ message.code ] ??
			(this.protocolSerializers[ message.code ] = buildSerializer("Object", message.payload));

		const length = baseLength + serializer(body, parts);
		const buffer = new Uint8Array(length);
		let offset = 0;
		for(let part of parts)
			if(typeof part === "number")
				buffer[ offset++ ] = part;
			else
			{
				buffer.set(part, offset);
				offset += part.length;
			}
		return buffer;
	}

	sendClientMessage(message : ProtocolMessage, body : any)
	{
		const parts : ByteOrByteArray[] = [message.code];
		return this.dataChannel!.send( this.serializeMessage(message, body, parts, 1) );
	}

	sendAvatarMessage(message : ProtocolMessage, body : any, time ? : number )
	{
		const parts : ByteOrByteArray[] = [message.code, Writer.writeUint64(time ?? Date.now()) ];
		return this.dataChannel!.send( this.serializeMessage(message, body, parts, 9) );
	}

	sendObjectMessage(message : ProtocolMessage, targetId : Guid, body : any, time ? : number )
	{
		const parts : ByteOrByteArray[] = [message.code, Writer.writeUint64(time ?? Date.now()), targetId.asBytes() ];
		return this.dataChannel!.send( this.serializeMessage(message, body, parts, 25) );
	}

	sendMessage( code : number | ProtocolMessage, ...parts : ByteOrByteArray[] ) : number
	{
		if( typeof(code) != "number" )
			code = code.code;
		const now = Date.now();
		this.sendRaw( code, Writer.writeUint64(now), ...parts );
		return now;
	}

	getForwardedTrack( emitter : Guid, mid : string ) : Promise<RemoteMediaStreamTrack>
	{
		return new Promise<RemoteMediaStreamTrack>( (yes, _) =>
		{
			for(let mid in this.tracks)
			{
				const track = this.tracks[mid];
				
				if(!track.sourceId) continue;
				if(!track.sourceId.equals(emitter)) continue;
				if(track.sourceMid != mid) continue;
				return track;
			}
			const listener = (event : CustomEvent) =>
			{
				const track = event.detail as RemoteMediaStreamTrack;
				if(track.sourceId?.equals(emitter) && (track.sourceMid == mid))
				{
					this.eventTarget.removeEventListener("track-forwarded", listener as EventListener);
					return yes(track);
				}
			};
			this.eventTarget.addEventListener("track-forwarded", listener as EventListener);
		});
	};

	connect( configUrl : string, connectUrl : string, prepare : (request : object) => object ) : Promise< RTCClient >
	{
		const get = () => prepare( { headers : {} } );
		const post = (body : any) => prepare( {
				method : "POST",
				body : JSON.stringify(body),
				headers : { "Content-Type" : "application/json" }
		} );

		const waitForStableSignaling = (connection : RTCPeerConnection) => new Promise<RTCPeerConnection>( (stable, closed) =>
		{
			if(connection.signalingState == "stable") return stable(connection);
			if(connection.signalingState == "closed") return closed(connection);
			const listener = () =>
			{
				let callback : any = null;
				if(connection.signalingState == "stable") callback = stable;
				else if(connection.signalingState == "closed") callback = closed;
				else return;
				connection.removeEventListener("signalingstatechange", listener);
				callback(connection);
			}
			connection.addEventListener("signalingstatechange", listener);
		});

		const localSdpOptions : RTCOfferOptions = 
		{
			offerToReceiveAudio : true,
			offerToReceiveVideo : true
		};

		const transformLocalSdp = (sdp : string) => sdp.split("\r\n").map( (row) =>
			{
				if( row.startsWith("a=fmtp:") && row.includes("useinbandfec=1") )
					row += "; stereo=1"; //Explicitly receive stereo audio
				return row;
			}).join("\r\n");
		
		const clicked = new Promise<Event>( (y,_) => window.addEventListener("pointerdown", y, {once:true}) );

		return new Promise<RTCClient>( async (connected, no) =>
		{
			const configResponse = await fetch(configUrl, get());

			const config = (await configResponse.json()) as { ice : any, protocol : ProtocolConfiguration };

			const connection = new RTCPeerConnection(config.ice);
			const dataChannel = connection.createDataChannel("data");
			dataChannel.binaryType = "arraybuffer";

			const connectionReady = new Promise<void>( (resolve, reject) =>
			{
				// Wait for ready, then remove all listeners and setup
				const checkReady = (_ : Event) =>
				{
					this.eventTarget.dispatchEvent(new CustomEvent("connection-progress", { detail : {
						signalingState : connection.signalingState,
						iceConnectionState : connection.iceConnectionState,
						dataChannelState : dataChannel.readyState,
						connectionState : connection.connectionState,
						iceGatheringState : connection.iceGatheringState
					} }));
					if(connection.signalingState != "stable") return;
					if(connection.iceConnectionState != "connected") return;
					if(connection.connectionState != "connected") return;
					if(dataChannel.readyState != "open") return;
					
					connection.removeEventListener("iceconnectionstatechange", checkReady);
					connection.removeEventListener("signalingstatechange", checkReady);
					connection.removeEventListener("connectionstatechange", checkReady);
					connection.removeEventListener("icegatheringstatechange", checkReady);
					dataChannel.removeEventListener("open", checkReady);
					resolve();
				}
				connection.addEventListener("iceconnectionstatechange", checkReady);
				connection.addEventListener("signalingstatechange", checkReady);
				connection.addEventListener("connectionstatechange", checkReady);
				connection.addEventListener("icegatheringstatechange", checkReady);
				dataChannel.addEventListener("open", checkReady);
			});

			
			const protocol : ClientProtocol = {} as any, deserializers : PayloadDeserializer[] = [];
			for(let message of config.protocol.client)
			{
				(protocol as any)[ message.name ] = message;
				deserializers[ message.code ] = buildDeserializer("Object", message.payload);
			}
			
			const offer : RTCSessionDescriptionInit = {
				type : "offer",
				sdp : transformLocalSdp( (await connection.createOffer(localSdpOptions)).sdp! ) 
			};

			const answerResponse = await fetch(connectUrl, post(offer) );
			const answer = await answerResponse.json() as RTCSessionDescriptionInit, location = answerResponse.headers.get("Location")!;

			this.connection = connection;
			this.dataChannel = dataChannel;
			this.protocol = config.protocol;

			this.eventTarget.dispatchEvent(new CustomEvent("connection-start", { detail : { connection, dataChannel } } ));

			const iceLocation = location + "/Ice/Candidates";
				
			// Transfer all ICE candidates to the backend
			const onIceCandidate = (event : RTCPeerConnectionIceEvent) => {
				if(event.candidate && event.candidate.candidate)
				{
					const body = {
						candidate : event.candidate.candidate,
						sdpMid : event.candidate.sdpMid,
						sdpMLineIndex : event.candidate.sdpMLineIndex,
						usernameFragment : event.candidate.usernameFragment
					};
					fetch(iceLocation, post(body));
				}
				else
					connection.removeEventListener("icecandidate", onIceCandidate);
			};
			connection.addEventListener("icecandidate", onIceCandidate);


			connection.addEventListener("track", (e) =>
			{
				const track = e.transceiver.receiver.track, mid = e.transceiver.mid;
				if(mid)
				{
					console.log("Received", track.kind, "track with mid", mid);
					this.tracks[mid] = track as RemoteMediaStreamTrack;
					
					//Tell remote that we've received the track.
					//If datachannel is not open, it's only the remote audio track.
					//(because any other track can only be obtained from joining channels, which is done through dataChannel)
					if(dataChannel.readyState == "open")
						this.sendClientMessage( protocol.ReceiveTrack, { mid } );
				}
				else
					console.warn("Received", track.kind, "track without a mid");
				
				if(track.kind == "audio")
				{
					const player = document.createElement("audio");
					player.srcObject = new MediaStream([track]);
					clicked.then( () => player.play() );
					document.body.append(player);
				}
			});

			await connection.setLocalDescription(offer);
			await connection.setRemoteDescription(answer);

		
			if(connection.signalingState != "stable")
				await new Promise<void>( (resolve, reject) => {
					const listener = (_ : Event) => {
						if( connection.signalingState == "stable" ) resolve();
						else if(connection.signalingState == "closed" ) reject();
						else return;
						connection.removeEventListener("signalingstatechange",  listener);	
					};
					connection.addEventListener("signalingstatechange",  listener);
				});

				
			const remoteIceCandidatesStream = new EventSource(iceLocation);
			remoteIceCandidatesStream.addEventListener("message", (event) =>
			{
				const iceCandidate = JSON.parse(event.data);
				connection.addIceCandidate(iceCandidate);
				if(!iceCandidate)
					remoteIceCandidatesStream.close();  //Null terminates the stream
			});
			const iceConnectionStateListener = (event : Event) =>
			{
				if(connection.iceConnectionState != "connected") return;
				remoteIceCandidatesStream.close();
				connection.removeEventListener("iceconnectionstatechange", iceConnectionStateListener);
			};
			connection.addEventListener("iceconnectionstatechange", iceConnectionStateListener);

			await connectionReady;

			connection.addEventListener("negotiationneeded", async () => 
			{
				await waitForStableSignaling(connection);
				const sdp : string = transformLocalSdp( (await connection.createOffer(localSdpOptions)).sdp! );
				await connection.setLocalDescription({ type : "offer", sdp});
				this.sendClientMessage(protocol.Offer, {sdp} );
			} );

			dataChannel.addEventListener("message", async (event) =>
			{	
				let src = event.data;
				src.arrayBuffer && (src = await src.arrayBuffer());
				
				let message = new Uint8Array(src as ArrayBuffer);
				const controlCode = message[0];
				message = message.subarray(1);

				const clientDeserializer = deserializers[ controlCode ];
				if(clientDeserializer === undefined) //Not for client
				{
					const timeView = new DataView(message.buffer, message.byteOffset, 8);
					const messageData : MessageData = {
						code : controlCode,
						time : (timeView.getUint32(0, true) + 0x100000000 * timeView.getUint32(4, true)),
						emitter : new Guid(message, 8),
						data : message.subarray(24)
					};
					return this.eventTarget.dispatchEvent(new CustomEvent("message", { detail : messageData }));
				}

				const messagePayload = clientDeserializer( {message} );

				if(controlCode == protocol.Offer.code) //New offer sdp from a server-side renegociation
				{
					const offer = messagePayload as OfferPayload;
					await waitForStableSignaling(connection);
					await connection.setRemoteDescription( { type:"offer", sdp:offer.sdp } );
					const sdp = transformLocalSdp( (await connection.createAnswer(localSdpOptions)).sdp! );
					this.sendClientMessage(protocol.Answer, {sdp} );
					await connection.setLocalDescription({ type:"answer", sdp });
				}
				else if(controlCode == protocol.Answer.code) //Anwser sdp in response of a client-side renegociation
				{
					const answer = messagePayload as AnswerPayload;
					await connection.setRemoteDescription({ type:"answer", sdp:answer.sdp });
				}
				else if(controlCode == protocol.ForwardTrack.code) //Track forwarding
				{
					const forwardTrack = messagePayload as ForwardTrackPayload;
					const track = this.tracks[ forwardTrack.mid ];
					if(!track)
						return console.warn("Received track forwarding info for an unknown mid", forwardTrack.mid );
					track.sourceId = forwardTrack.emitterId;
					track.sourceMid = forwardTrack.emitterMid;
					this.eventTarget.dispatchEvent(new CustomEvent("track-forwarded", { detail : track } ));
				}
				else if(controlCode == protocol.ReceiveTrack.code ) //Track received acknowledgement
				{
					const receiveTrack = messagePayload as ReceiveTrackPayload;
					this.eventTarget.dispatchEvent(new CustomEvent("track-received", { detail : receiveTrack.mid }));
				} 
				else if(controlCode == protocol.Ready.code)
				{
					this.eventTarget.dispatchEvent(new CustomEvent("client-ready"));
				}
				else if(controlCode == protocol.Error.code) //Error
				{
					console.warn("Rtc Error", (messagePayload as ErrorPayload).message );
				}
			});

			this.sendClientMessage(protocol.Ready, {} );

			connected(this);

		} );
	}
}


